import { useEffect, useState } from 'react';
import {
  Card, Container, Field, Grid, GridItem,
  Input, Button, Separator, Text,
  Flex, Tag, HStack,
} from '@chakra-ui/react';
import {
  capitalizeFirstLetter,
  formatMoney,
  formatPhoneNumber,
  onlyDigits,
  parseStringNumberToFloat,
} from '@utils/formats';
import { formatDate, toISODateString } from '@utils/date';
import { toast } from 'react-toastify';
import { PHONE_NUMBER } from '@utils/regex';
import { useNavigate } from 'react-router';
import type { PortfolioType } from '@customTypes/portfolio';
import type { FormErrors } from '@customTypes/errors';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import CpfInput from '@components/CpfInput';
import DateInput from '@components/DateInput';
import PhoneInput from '@components/PhoneInput';
import CurrencyField from '@components/CurrencyInput';
import useGetPortfolioList from '@hooks/useGetPortfolioList';
import Loading from '@components/Loading';
import PortfolioSelect from '@components/PortfolioSelect';

import useGetCustomer from './useGetCustomer';

const Upsert = () => {
  const navigate = useNavigate();
  const isEditing = window.location.pathname.includes('editar');

  const {
    getPortfolios,
    portfolios,
  } = useGetPortfolioList();

  const { customer, loading: customerLoading } = useGetCustomer();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [currentPhone, setCurrentPhone] = useState('');
  const [phones, setPhones] = useState<string[]>([]);
  const [consignableMargin, setConsignableMargin] = useState('');
  const [payrollCreditCardLimit, setPayrollCreditCardLimit] = useState('');
  const [benefitCardLimit, setBenefitCardLimit] = useState('');
  const [currentTag, setCurrentTag] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioType | null>(null);

  const handleAddPhone = () => {
    if (currentPhone && PHONE_NUMBER.test(currentPhone)) {
      setPhones((prevPhones) => [...prevPhones, currentPhone]);
      setCurrentPhone('');
    }
  };

  const handleRemovePhone = (index: number) => {
    setPhones((prevPhones) => prevPhones.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (currentTag) {
      setTags((prevTags) => [...prevTags, currentTag]);
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags((prevTags) => prevTags.filter((_, i) => i !== index));
  };

  const onCreate = async (variables: { [k: string]: unknown }) => {
    try {
      const response = await api.post('/customer', variables);

      if (response?.status === 200) {
        toast.success('Cliente criado com sucesso!');
        navigate(`/painel/clientes/${response.data.id}`);
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch {
      toast.error('Erro ao criar o cliente. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    };
  };

  const onEdit = async (variables: { [k: string]: unknown }) => {
    try {
      const response = await api.put(`/customer/${customer?.id}`, variables);

      if (response?.status === 200) {
        toast.success('Cliente editado com sucesso!');
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch {
      toast.error('Erro ao editar o cliente. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    };
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());
    
    if (phones.length === 0) {
      setErrors({ phone: ['Adicione ao menos um telefone'] });
      setLoading(false);
      return;
    }
    
    if (tags.length === 0) {
      setErrors({ tags: ['Adicione ao menos uma tag'] });
      setLoading(false);
      return;
    }

    if (!selectedPortfolio) {
      setErrors({ portfolio: ['Selecione um portfólio'] });
      return;
    }

    setLoading(true);

    const variables = {
      name: capitalizeFirstLetter(data.name as string),
      tax_id: onlyDigits(data.tax_id as string),
      email: data.email,
      portfolio_id: selectedPortfolio?.id,
      consignable_margin: parseStringNumberToFloat(consignableMargin),
      payroll_credit_card_limit: parseStringNumberToFloat(payrollCreditCardLimit),
      benefit_card_limit: parseStringNumberToFloat(benefitCardLimit),
      registration: data.registration,
      tenure: data.tenure,
      tags,
      birth_date: toISODateString(data.birth_date as string),
      phone_numbers: phones.map((phone) => onlyDigits(phone)),
    };

    if (isEditing) {
      await onEdit(variables);
      return;
    }

    await onCreate(variables);
  };

  useEffect(() => {
    getPortfolios({});
  }, [getPortfolios]);

  useEffect(() => {
    if (isEditing && customer) {
      setPhones(customer.phone_numbers.map((item) => formatPhoneNumber(item.phone_number)) || []);
      setConsignableMargin(formatMoney(customer.consignable_margin));
      setPayrollCreditCardLimit(formatMoney(customer.payroll_credit_card_limit));
      setBenefitCardLimit(formatMoney(customer.benefit_card_limit));
      setTags(customer.tags || []);
      setSelectedPortfolio(customer.portfolio as unknown as PortfolioType);
    }
  }, [isEditing, customer]);

  if (isEditing && customerLoading) {
    return <Loading />;
  }

  if (isEditing && !customer) {
    return (
      <Flex p={4} justifyContent="center">
        <Text>Cliente não encontrado!</Text>
      </Flex>
    );
  }

  return (
    <Container maxW="5xl">
      <Card.Root
        size="sm"
        bg="grayscale.fg"
        variant="subtle"
        p={4}
      >
        <form onSubmit={onSubmit}>
          <Text fontSize="md" fontWeight="bold">
            Dados do cliente
          </Text>
          <Separator mb={6} mt={1} />
          <Grid templateColumns="repeat(4, 1fr)" gap="5">
            <GridItem colSpan={{ base: 4, lg: 3 }}>
              <Field.Root invalid={!!errors?.name}>
                <Field.Label>Nome</Field.Label>
                <Input
                  name="name"
                  placeholder="Digite aqui..."
                  defaultValue={customer?.name || ''}
                  minLength={3}
                  variant="subtle"
                  required
                />
                {errors?.name?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
            <GridItem colSpan={{ base: 4, lg: 1 }}>
              <Field.Root invalid={!!errors?.birth_date}>
                <Field.Label>Data de nascimento</Field.Label>
                <DateInput
                  name="birth_date"
                  placeholder="dd/mm/aaaa"
                  defaultValue={formatDate(customer?.birth_date) || ''}
                  variant="subtle"
                  required
                />
                {errors?.birth_date?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
            <GridItem colSpan={{ base: 4, lg: 2 }}>
              <Field.Root invalid={!!errors?.email}>
                <Field.Label>E-mail</Field.Label>
                <Input
                  name="email"
                  placeholder="email@exemplo.com"
                  defaultValue={customer?.email || ''}
                  variant="subtle"
                />
                {errors?.email?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
            <GridItem colSpan={{ base: 4, lg: 2 }}>
              <Field.Root invalid={!!errors?.tax_id}>
                <Field.Label>CPF</Field.Label>
                <CpfInput
                  name="tax_id"
                  placeholder="000.000.000-00"
                  defaultValue={customer?.tax_id || ''}
                  variant="subtle"
                  required
                />
                {errors?.tax_id?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
            <GridItem colSpan={4}>
              <Field.Root invalid={!!errors?.phone}>
                <Field.Label>Telefone(s)</Field.Label>
                <HStack gap={2} flexWrap="wrap">
                  {phones.map((phone, index) => (
                    <Tag.Root size="xl" key={index} borderRadius="lg">
                      <Tag.Label>{phone}</Tag.Label>
                      <Tag.EndElement>
                        <Tag.CloseTrigger
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemovePhone(index)}
                        />
                      </Tag.EndElement>
                    </Tag.Root>
                  ))}
                </HStack>
                <PhoneInput
                  value={currentPhone}
                  onChange={(e) => setCurrentPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPhone();
                    }
                  }}
                  placeholder="(00) 00000-0000"
                  variant="subtle"
                  maxWidth={{ base: '100%', lg: '49%' }}
                />
                <Field.HelperText>Pressione Enter para adicionar o telefone</Field.HelperText>
                {errors?.phone?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
          </Grid>

          <Text fontSize="md" fontWeight="bold" mt={8}>
            Margens
          </Text>
          <Separator mb={6} mt={1} />
          <Grid templateColumns="repeat(3, 1fr)" gap="5">
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <CurrencyField
                value={consignableMargin}
                onChange={setConsignableMargin}
                errors={errors?.consignable_margin}
                label="Consignável"
              />
            </GridItem>
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <CurrencyField
                value={payrollCreditCardLimit}
                onChange={setPayrollCreditCardLimit}
                errors={errors?.payroll_credit_card_limit}
                label="Cartão consignado"
              />
            </GridItem>
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <CurrencyField
                value={benefitCardLimit}
                onChange={setBenefitCardLimit}
                errors={errors?.benefit_card_limit}
                label="Cartão benefícios"
              />
            </GridItem>
          </Grid>

          <Text fontSize="md" fontWeight="bold" mt={8}>
            Informações contratuais
          </Text>
          <Separator mb={6} mt={1} />
          <Grid templateColumns="repeat(3, 1fr)" gap="5">
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <PortfolioSelect
                errors={errors}
                portfolios={portfolios}
                onSearch={async (text) => await getPortfolios({ query: text })}
                value={selectedPortfolio}
                onChange={(portfolio) => setSelectedPortfolio(portfolio)}
              />
              {errors?.portfolio?.map((error, index) => (
                <ErrorLabel key={index} text={error} />
              ))}
            </GridItem>
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <Field.Root invalid={!!errors?.registration}>
                <Field.Label>Matrícula</Field.Label>
                <Input
                  name="registration"
                  placeholder="Digite aqui..."
                  defaultValue={customer?.registration || ''}
                  variant="subtle"
                  required
                />
                {errors?.registration?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
            <GridItem colSpan={{ base: 3, md: 1 }}>
              <Field.Root invalid={!!errors?.tenure}>
                <Field.Label>Vínculo</Field.Label>
                <Input
                  name="tenure"
                  placeholder="Digite aqui..."
                  defaultValue={customer?.tenure || ''}
                  variant="subtle"
                  required
                />
                {errors?.tenure?.map((error, index) => (
                  <Field.ErrorText key={index}>{error}</Field.ErrorText>
                ))}
              </Field.Root>
            </GridItem>
          </Grid>

          <Text fontSize="md" fontWeight="bold" mt={8}>
            Tags
          </Text>
          <Separator mb={6} mt={1} />
          <Field.Root invalid={!!errors?.tags}>
            <HStack gap={2} flexWrap="wrap">
              {tags.map((tag, index) => (
                <Tag.Root size="xl" key={index} borderRadius="lg">
                  <Tag.Label>{tag}</Tag.Label>
                  <Tag.EndElement>
                    <Tag.CloseTrigger
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRemoveTag(index)}
                    />
                  </Tag.EndElement>
                </Tag.Root>
              ))}
            </HStack>
            <Input
              value={currentTag}
              onChange={(e) => setCurrentTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="Digite uma tag e pressione Enter"
              variant="subtle"
              maxWidth={{ base: '100%', lg: '49%' }}
            />
            {errors?.tags?.map((error, index) => (
              <Field.ErrorText key={index}>{error}</Field.ErrorText>
            ))}
          </Field.Root>

          {errors?.general && (
            <Flex direction="column" my={4}>
              {errors.general.map((error, index) => (
                <ErrorLabel key={index} text={error} />
              ))}
            </Flex>
          )}

          <Flex justify="space-between" mt={6}>
            <Button variant="outline" disabled={loading} bg="bg.muted" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
            <Button bg="green.700" color="white" type="submit" loading={loading}>
              {isEditing ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          </Flex>
        </form>
      </Card.Root>
    </Container>
  );
};

export default Upsert;
